import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useHttpClient, useNetworkStatus } from '../../contexts';
import { useReducerStorage as useReducer, useToast } from '../../hooks';
import {
  EquipmentMetric,
  EquipmentType,
  SystemType,
} from './systemTypes.models';
import {
  initState,
  systemTypesReducer,
  SystemTypesState,
} from './systemTypes.reducer';
import { getSystemMetricsOptions } from './systemTypes.utils';

type OwnProps = PropsWithChildren<{
  initialState?: Partial<SystemTypesState>;
}>;

interface SystemTypesContextProps {
  systemTypes: SystemType[];
  equipmentTypes: EquipmentType[];
  metrics: {
    raw: EquipmentMetric[];
    efficiency: { heating: string[]; cooling: string[] };
    load: { heating: string[]; cooling: string[] };
  };
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchSystemTypes: () => Promise<void>;
}

const SystemTypesContext = createContext<SystemTypesContextProps | undefined>(
  undefined
);

export const SystemTypesProvider: React.FC<OwnProps> = ({ children }) => {
  const [state, dispatch] = useReducer(systemTypesReducer, initState, {
    key: 'airship-cache:system-types',
    select: (s) => ({
      ids: s.ids,
      entities: s.entities,
      metrics: s.metrics,
      equipmentTypes: s.equipmentTypes,
    }),
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [showToast, state.error]);

  const systemTypes = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter((st) => st.status === 'ACTIVE'),
    [state.entities, state.ids]
  );

  const fetchSystemTypes = useCallback(async () => {
    if (!isOnline()) {
      dispatch({
        type: 'SET_LOADING_STATE',
        payload: { isLoading: false, isLoaded: true },
      });
      return;
    }
    dispatch({ type: 'FETCH_SYSTEM_OPTIONS' });

    try {
      const [systemTypes, metrics, equipmentTypes] = await Promise.all([
        httpClient.get<SystemType[]>('/equipment/system-types', {
          headers: { Authorization: undefined },
        }),
        httpClient.get<EquipmentMetric[]>('/equipment/metrics', {
          headers: { Authorization: undefined },
        }),
        httpClient.get<EquipmentType[]>('/equipment/types', {
          headers: { Authorization: undefined },
        }),
      ]);

      const {
        heatingCapacityOptions,
        coolingCapacityOptions,
        heatingEfficiencyOptions,
        coolingEfficiencyOptions,
      } = getSystemMetricsOptions(metrics);

      dispatch({
        type: 'FETCH_SYSTEM_OPTIONS_SUCCESS',
        payload: {
          systemTypes,
          metrics: {
            raw: metrics,
            efficiency: {
              heating: heatingEfficiencyOptions,
              cooling: coolingEfficiencyOptions,
            },
            load: {
              heating: heatingCapacityOptions,
              cooling: coolingCapacityOptions,
            },
          },
          equipmentTypes,
        },
      });
    } catch (error) {
      dispatch({
        type: 'FETCH_SYSTEM_OPTIONS_FAILURE',
        payload: { error: `${error}` },
      });
    }
  }, [httpClient, isOnline]);

  return (
    <SystemTypesContext.Provider
      value={{
        systemTypes,
        equipmentTypes: state.equipmentTypes,
        metrics: state.metrics,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        fetchSystemTypes,
      }}
    >
      {children}
    </SystemTypesContext.Provider>
  );
};

export const useSystemTypes = () => {
  const context = useContext(SystemTypesContext);
  if (!context) {
    throw new Error('useSystemTypes must be used within a SystemTypesProvider');
  }
  return context;
};
